import { useState, useEffect, useCallback, useRef } from 'react';
import { Users, RefreshCw, AlertCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../../auth';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { EmployeeList } from './EmployeeList';
import { EmployeeDetail } from './EmployeeDetail';
import { HRExportPanel } from './HRExportPanel';
import { CreateEmployeeDialog } from './CreateEmployeeDialog';
import { TeamMemberCalendar } from '../Manager/TeamMemberCalendar';
import { EmployeeViewTabs, TAB_BUTTON_ID, TAB_PANEL_ID } from '../common/EmployeeViewTabs';
import { getCurrentPayrollMonth } from '../../utils/payrollPeriod';
import { getCompanyVacationDates, mergeCompanyVacationIntoEntries } from '../../utils/companyVacation';
import type { DayEntry, UserEntry, MonthlyApproval, CompanyVacationRule, WageType } from '../../types';
import { Card, CardContent } from '@/components/ui/card';

export function HRDashboard() {
  const { isHR } = useAuth();
  const { getAllUsers, getUserEntries, deleteUser, getApprovals, setApproval, getCompanyVacationRule } = useAPIStorage();

  const [users, setUsers] = useState<UserEntry[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State für Kalender-Bearbeitung
  const [calendarUser, setCalendarUser] = useState<UserEntry | null>(null);
  const [calendarEntries, setCalendarEntries] = useState<Record<string, DayEntry>>({});
  const [previousYearEntries, setPreviousYearEntries] = useState<Record<string, DayEntry>>({});
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  // Tab-Umschaltung Details/Kalender
  const [employeeViewTab, setEmployeeViewTab] = useState<'details' | 'calendar'>('details');

  // Mitarbeiter-Anlegen Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Freigabe-State (zentral für Listenansicht)
  const [approvalMonth, setApprovalMonth] = useState(getCurrentPayrollMonth);
  const [approvals, setApprovals] = useState<MonthlyApproval[]>([]);
  const [listViewMode, setListViewMode] = useState<'list' | 'grid'>('grid');

  const currentYear = new Date().getFullYear();

  // Betriebsferien-Regel cachen (einmal laden bei Kalender-Öffnung)
  const companyVacationRuleRef = useRef<CompanyVacationRule | null>(null);

  /** Betriebsferien mit dem WageType des betrachteten Mitarbeiters mergen */
  const applyCompanyVacationForUser = useCallback((
    entries: Record<string, DayEntry>,
    year: number,
    wageType: WageType | null | undefined,
  ): Record<string, DayEntry> => {
    const rule = companyVacationRuleRef.current;
    if (!rule) return entries;
    const dates = getCompanyVacationDates(rule, year);
    return mergeCompanyVacationIntoEntries(entries, dates, wageType ?? null);
  }, []);

  // Refs für popstate-Handler (vermeidet stale closures)
  const calendarUserRef = useRef(calendarUser);
  calendarUserRef.current = calendarUser;
  const selectedUserIdRef = useRef(selectedUserId);
  selectedUserIdRef.current = selectedUserId;

  // Browser-Zurück: Sub-Views schließen
  useEffect(() => {
    const handlePopState = () => {
      if (selectedUserIdRef.current || calendarUserRef.current) {
        setSelectedUserId(null);
        setCalendarUser(null);
        setCalendarYear(currentYear);
        setEmployeeViewTab('details');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentYear]);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('HRDashboard: Lade Benutzerliste...');
      const userList = await getAllUsers();
      console.log('HRDashboard: Geladene Benutzer:', userList);
      setUsers(userList);
    } catch (e) {
      console.error('HRDashboard: Fehler beim Laden:', e);
      setError(e instanceof Error ? e.message : 'Fehler beim Laden der Mitarbeiter');
    } finally {
      setIsLoading(false);
    }
  }, [getAllUsers]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  // Approvals laden wenn Listenansicht aktiv oder Monat wechselt
  const loadApprovals = useCallback(async () => {
    if (listViewMode !== 'list') return;
    try {
      const data = await getApprovals(approvalMonth.year, approvalMonth.month);
      setApprovals(data);
    } catch (e) {
      console.error('Fehler beim Laden der Freigaben:', e);
    }
  }, [getApprovals, approvalMonth, listViewMode]);

  useEffect(() => {
    void loadApprovals();
  }, [loadApprovals]);

  // Freigabe-Handler: wird von EmployeeList aufgerufen.
  // Re-throw ist Absicht: EmployeeList.handleCheckboxChange catch't und
  // ruft showApprovalFeedback(..., 'error') — ohne Re-throw feedbackt
  // der Caller faelschlich Erfolg. Globale Rejection-Sicherheit: der
  // einzige Caller (EmployeeList) wrappt den Call in try/catch.
  const handleApprovalChange = async (userId: string, approved: boolean, note: string) => {
    try {
      await setApproval(userId, approvalMonth.year, approvalMonth.month, {
        approved,
        approvalNote: note || null,
      });
      const updated = await getApprovals(approvalMonth.year, approvalMonth.month);
      setApprovals(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Freigabe konnte nicht gespeichert werden');
      throw e;
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      console.error('Fehler beim Löschen:', e);
      setError(e instanceof Error ? e.message : 'Fehler beim Löschen des Benutzers');
    }
  };

  // Kalenderdaten laden (ohne History-Manipulation)
  const calendarLoadIdRef = useRef(0);
  const loadCalendarData = useCallback(async (user: UserEntry) => {
    const loadId = ++calendarLoadIdRef.current;
    setIsLoadingCalendar(true);
    try {
      setCalendarYear(currentYear);
      const [currentEntries, previousEntries, vacationRule] = await Promise.all([
        getUserEntries(user.id, currentYear),
        getUserEntries(user.id, currentYear - 1),
        getCompanyVacationRule(),
      ]);
      // Veraltete Ergebnisse ignorieren (schneller User-Wechsel)
      if (calendarLoadIdRef.current !== loadId) return;
      companyVacationRuleRef.current = vacationRule;
      const mergedEntries = applyCompanyVacationForUser(currentEntries || {}, currentYear, user.wageType);
      setCalendarEntries(mergedEntries);
      setPreviousYearEntries(previousEntries || {});
      setCalendarUser(user);
    } catch (e) {
      if (calendarLoadIdRef.current !== loadId) return;
      console.error('Fehler beim Laden der Kalenderdaten:', e);
      setError('Fehler beim Laden der Kalenderdaten');
    } finally {
      if (calendarLoadIdRef.current === loadId) {
        setIsLoadingCalendar(false);
      }
    }
  }, [currentYear, getUserEntries, getCompanyVacationRule, applyCompanyVacationForUser]);

  // Handler für Kalender-Bearbeitung (aus Liste)
  const handleOpenCalendar = async (user: UserEntry) => {
    setSelectedUserId(user.id);
    setEmployeeViewTab('calendar');
    await loadCalendarData(user);
    window.history.pushState({ tab: 'hr', view: 'employee', userId: user.id }, '');
  };

  // Handler für Jahreswechsel im Kalender
  const handleYearChange = async (newYear: number) => {
    if (!calendarUser) return;
    setIsLoadingCalendar(true);
    try {
      setCalendarYear(newYear);
      // Lade neues Jahr und dessen Vorjahr parallel für 13-Wochen-Berechnung
      const [currentEntries, previousEntries] = await Promise.all([
        getUserEntries(calendarUser.id, newYear),
        getUserEntries(calendarUser.id, newYear - 1),
      ]);
      const mergedEntries = applyCompanyVacationForUser(currentEntries || {}, newYear, calendarUser.wageType);
      setCalendarEntries(mergedEntries);
      setPreviousYearEntries(previousEntries || {});
    } catch (e) {
      console.error('Fehler beim Laden der Kalenderdaten:', e);
      setError('Fehler beim Laden der Kalenderdaten');
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  // Zurück zur Mitarbeiterliste
  const handleBackToList = () => {
    window.history.back();
  };

  // Tab-Wechsel zwischen Details und Kalender
  const handleEmployeeTabChange = async (tab: 'details' | 'calendar') => {
    if (tab === 'calendar' && !calendarUser) {
      const user = users.find((u) => u.id === selectedUserId);
      if (user) {
        await loadCalendarData(user);
      }
    }
    setEmployeeViewTab(tab);
  };

  // Handler nach Kalender-Refresh
  const handleCalendarRefresh = async () => {
    if (calendarUser) {
      const entries = await getUserEntries(calendarUser.id, calendarYear);
      const mergedEntries = applyCompanyVacationForUser(entries || {}, calendarYear, calendarUser.wageType);
      setCalendarEntries(mergedEntries);
    }
  };

  if (!isHR) {
    return (
      <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
          Kein Zugriff
        </h2>
        <p className="text-red-600 dark:text-red-300">
          Diese Seite ist nur für Mitarbeiter der Personalabteilung zugänglich.
        </p>
      </div>
    );
  }

  const selectedUser = users.find((u) => u.id === selectedUserId);

  const handleUserUpdated = (updated: UserEntry) => {
    setUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u));
  };

  // Mitarbeiter ausgewählt: Tab-Ansicht (Details / Kalender)
  if (selectedUser) {
    const tabBar = (
      <EmployeeViewTabs
        activeTab={employeeViewTab}
        onTabChange={handleEmployeeTabChange}
      />
    );

    return (
      <>
        <div
          id={TAB_PANEL_ID.details}
          role="tabpanel"
          aria-labelledby={TAB_BUTTON_ID.details}
          className={employeeViewTab !== 'details' ? 'hidden' : ''}
        >
          <EmployeeDetail
            user={selectedUser}
            onBack={handleBackToList}
            onUserUpdated={handleUserUpdated}
            tabBar={tabBar}
          />
        </div>
        {calendarUser && (
          <div
            id={TAB_PANEL_ID.calendar}
            role="tabpanel"
            aria-labelledby={TAB_BUTTON_ID.calendar}
            className={employeeViewTab !== 'calendar' ? 'hidden' : ''}
          >
            <TeamMemberCalendar
              userId={calendarUser.id}
              userName={calendarUser.name}
              year={calendarYear}
              entries={calendarEntries}
              previousYearEntries={previousYearEntries}
              onBack={handleBackToList}
              onRefresh={handleCalendarRefresh}
              canEditEntries={true}
              onYearChange={handleYearChange}
              viewedUserWageType={calendarUser.wageType}
              tabBar={tabBar}
              employmentStart={calendarUser.employmentStart}
              employmentEnd={calendarUser.employmentEnd}
            />
          </div>
        )}
        {/* Loading Indicator für Kalender */}
        {isLoadingCalendar && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-40">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Lade Kalenderdaten...</span>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-gray-100 dark:bg-gray-800 p-1.5">
                <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                  HR-Dashboard
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Übersicht aller Mitarbeiter-Urlaubsdaten
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm
                           bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200 rounded-lg transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Mitarbeiter anlegen
              </button>
              <button
                onClick={loadUsers}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-2 text-sm
                           border border-gray-300 dark:border-gray-600 rounded-lg
                           hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                           text-gray-700 dark:text-gray-200
                           disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Lohn-Export */}
      {!isLoading && users.length > 0 && (
        <HRExportPanel users={users} />
      )}

      <EmployeeList
        users={users}
        selectedUserId={selectedUserId}
        onSelectUser={(userId) => {
          setSelectedUserId(userId);
          setEmployeeViewTab('details');
          setCalendarUser(null);
          if (userId) {
            window.history.pushState({ tab: 'hr', view: 'employee', userId }, '');
          }
        }}
        onDeleteUser={handleDeleteUser}
        onCalendarEdit={handleOpenCalendar}
        isLoading={isLoading}
        isHR={true}
        approvalMonth={approvalMonth}
        onApprovalMonthChange={setApprovalMonth}
        approvals={approvals}
        onApprovalChange={handleApprovalChange}
        onViewModeChange={setListViewMode}
      />

      {/* Hinweis */}
      <div className="border border-blue-300/60 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-900/10 rounded-lg p-4">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>Hinweis:</strong> Als HR-Mitarbeiter haben Sie Lesezugriff auf alle Mitarbeiterdaten.
          Klicken Sie auf einen Mitarbeiter, um Details zu sehen. Mit dem Kalender-Icon können Sie
          Kalendereinträge direkt bearbeiten.
        </p>
      </div>

      {/* Mitarbeiter anlegen Dialog */}
      <CreateEmployeeDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={(newUser) => {
          setUsers((prev) => [...prev, newUser]);
        }}
      />

    </div>
  );
}
